import type { RideDef } from '@runkr/config'
import type { EventSink } from '../events'
import { NO_TICK } from '../world'
import type { PlayerState, World } from '../world'

/**
 * Rides (D-050, PHASE3 T3.10, D-056): a meter charged by the runner's own shards, a summon
 * (PET press with a full meter) with a short wind-up, then `rideDurationTicks` of riding: extra
 * speed, immunity to hazards and traps, and one ability per ride. Pure state helpers; the rules
 * that read them live where the step uses them (input, move, collide, hit, `rides/contacts.ts`).
 */

/** Riding at `tick` (the window is exclusive at `ridingUntil`). */
export function isRiding(p: PlayerState, tick: number): boolean {
  return p.ridingUntil > tick
}

/** Underground (Drillmole): untouchable, cannot jump. */
export function isBurrowed(p: PlayerState, tick: number): boolean {
  return p.burrowUntil > tick
}

/** The ride `p` is on at `tick`, or null. */
export function activeRide(world: World, p: PlayerState, tick = world.tick): RideDef | null {
  if (p.rideId === null || !isRiding(p, tick)) return null
  return world.cfg.rides.byId[p.rideId]
}

/** The meter is full and a summon may start. */
export function canSummon(world: World, p: PlayerState): boolean {
  const { tick } = world
  return (
    p.rideId !== null &&
    p.rideCharge >= world.cfg.rides.chargeShards &&
    !isRiding(p, tick) &&
    p.summonAtTick === NO_TICK &&
    p.stunnedUntil <= tick &&
    p.skidUntil <= tick &&
    p.respawnAtTick === NO_TICK &&
    p.placement === 0
  )
}

/** Step 1: a PET press with a full meter starts the wind-up. */
export function summon(world: World, i: number, events: EventSink): void {
  const p = world.players[i]!
  p.summonAtTick = world.tick + world.cfg.rides.summonTicks
  events.push({ type: 'rideSummon', tick: world.tick, player: i })
}

/**
 * Step 1, before input: the wind-up ends → riding starts (meter emptied); the window ends →
 * dismount (`rideEnd`, `dismountInvulnTicks`, the air jump back).
 */
export function updateRide(world: World, i: number, events: EventSink): void {
  const p = world.players[i]!
  const { tick } = world
  const r = world.cfg.rides
  if (p.ridingUntil > 0 && p.ridingUntil === tick) {
    endRide(world, i, events)
    p.invulnUntil = Math.max(p.invulnUntil, tick + r.dismountInvulnTicks)
    p.jumpsLeft = 1
  }
  if (p.summonAtTick !== NO_TICK && tick >= p.summonAtTick && p.rideId !== null) {
    p.summonAtTick = NO_TICK
    p.ridingUntil = tick + r.rideDurationTicks
    p.rideCharge = 0
    p.bumpedMask = 0
    events.push({ type: 'rideStart', tick, player: i, ride: p.rideId })
  }
}

/** Stop riding now (dismount, knocked off by a projectile, or a fall). */
export function endRide(world: World, i: number, events: EventSink): void {
  const p = world.players[i]!
  const wasRiding = p.ridingUntil > world.tick || p.ridingUntil === world.tick
  p.ridingUntil = 0
  p.burrowUntil = 0
  p.bumpedMask = 0
  if (wasRiding) events.push({ type: 'rideEnd', tick: world.tick, player: i })
}

/** A shard picked up: one point on the meter while not riding, capped at full. */
export function chargeRide(world: World, p: PlayerState): void {
  if (isRiding(p, world.tick)) return
  p.rideCharge = Math.min(world.cfg.rides.chargeShards, p.rideCharge + 1)
}

/**
 * Speed bonus (percent over base) for a runner: a ride's `speedPct` plus any boost, capped at
 * `maxSpeedPct` while riding; the boost alone otherwise.
 */
export function bonusPct(world: World, p: PlayerState, boostPct: number, tick: number): number {
  const ride = activeRide(world, p, tick)
  if (!ride) return boostPct
  return Math.min(world.cfg.rides.maxSpeedPct, ride.speedPct + boostPct)
}
